import { showToast } from './ui/toast.js';
import { TOASTS } from './config/toasts.js';
import { UI_COPY } from './config/uiCopy.js';
import { state } from './state.js';
import * as API from './api.js';

let _createLibResolve = null;
let _privacyResolve = null;
let _confirmResolve = null;
let _textPromptResolve = null;
let _planCompareResolve = null;
const BILLING_RETURN_CONTEXT_KEY = 'mosaic_billing_return_context_v1';

function _isCoarsePointerContext() {
    try {
        return Boolean(
            window.matchMedia('(hover: none)').matches ||
            window.matchMedia('(pointer: coarse)').matches
        );
    } catch (_e) {
        return false;
    }
}

function _focusInput(el, { force = false } = {}) {
    if (!el) return false;
    if (!force && _isCoarsePointerContext()) return false;
    try {
        el.focus({ preventScroll: true });
        return true;
    } catch (_e) {}
    try {
        el.focus();
        return true;
    } catch (_e) {
        return false;
    }
}

export function isAnyModalOpen() {
    return Boolean(document.querySelector('.modal:not(.hidden)'));
}

export function openCreateLibraryModal() {
    const modal = document.getElementById('createLibModal');
    const nameEl = document.getElementById('newLibName');
    const btnCancel = document.getElementById('createLibCancel');
    const btnCreate = document.getElementById('createLibCreate');
    const seg = document.getElementById('newLibPrivacy');
    const privacyField = document.getElementById('newLibPrivacyField');

    if (!modal || !nameEl || !btnCancel || !btnCreate || !seg) {
        return Promise.resolve(null);
    }
    const coarsePointer = _isCoarsePointerContext();
    const openedScrollY = Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0));

    const restoreMobileScroll = (targetY) => {
        if (!coarsePointer) return;
        const safeY = Math.max(0, Math.round(Number(targetY || 0)));
        const apply = () => {
            try {
                window.scrollTo({ top: safeY, left: 0, behavior: 'auto' });
            } catch (_e) {
                window.scrollTo(0, safeY);
            }
        };
        apply();
        window.requestAnimationFrame(apply);
        window.setTimeout(apply, 90);
    };

    nameEl.value = '';
    const isAdmin = Boolean(state.isAdmin);
    if (privacyField) {
        privacyField.classList.toggle('hidden', !isAdmin);
    }
    const buttons = Array.from(seg.querySelectorAll('.seg-btn'));
    buttons.forEach((b) => b.classList.remove('active'));
    const privateBtn = buttons.find((b) => b.dataset.value === 'private');
    if (privateBtn) privateBtn.classList.add('active');

    const close = (result = null) => {
        modal.classList.add('hidden');
        modal.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', onKeyDown);
        btnCancel.onclick = null;
        btnCreate.onclick = null;
        seg.onclick = null;
        if (result && result.name) {
            restoreMobileScroll(openedScrollY);
        }
        if (_createLibResolve) _createLibResolve(result);
        _createLibResolve = null;
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') close(null);
        if (e.key === 'Enter') btnCreate.click();
    };

    seg.onclick = (e) => {
        if (!isAdmin) return;
        const t = e.target.closest('.seg-btn');
        if (!t) return;
        buttons.forEach((b) => b.classList.remove('active'));
        t.classList.add('active');
    };

    btnCancel.onclick = () => close(null);
    btnCreate.onclick = () => {
        const name = String(nameEl.value || '').trim();
        if (!name) {
            const toast = TOASTS.libraryNameRequired;
            showToast(toast.title, toast.message, toast.duration);
            _focusInput(nameEl, { force: false });
            return;
        }
        const active = seg.querySelector('.seg-btn.active');
        const isPublic = isAdmin && Boolean(active && active.dataset.value === 'public');
        close({ name, isPublic });
    };

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    const autofocusApplied = _focusInput(nameEl, { force: false });
    API.logUiFlow('modal', 'create_library_focus_policy', 'ok', {
        coarse_pointer: coarsePointer,
        autofocus_applied: autofocusApplied,
    }).catch(() => {});
    document.addEventListener('keydown', onKeyDown);

    return new Promise((resolve) => { _createLibResolve = resolve; });
}

export function openPrivacyModal(libraryName, currentIsPublic = false) {
    if (!state.isAdmin) {
        return Promise.resolve(null);
    }
    const modal = document.getElementById('privacyModal');
    const desc = document.getElementById('privacyDesc');
    const seg = document.getElementById('privacySeg');
    const btnCancel = document.getElementById('privacyCancel');
    const btnSave = document.getElementById('privacySave');

    if (!modal || !seg || !btnCancel || !btnSave) {
        return Promise.resolve(null);
    }

    if (desc) desc.innerText = UI_COPY.modals.privacyDescription(libraryName);

    const buttons = Array.from(seg.querySelectorAll('.seg-btn'));
    buttons.forEach((b) => b.classList.remove('active'));
    const activeValue = currentIsPublic ? 'public' : 'private';
    const activeBtn = buttons.find((b) => b.dataset.value === activeValue);
    if (activeBtn) activeBtn.classList.add('active');

    const close = (result = null) => {
        modal.classList.add('hidden');
        modal.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', onKeyDown);
        btnCancel.onclick = null;
        btnSave.onclick = null;
        seg.onclick = null;
        if (_privacyResolve) _privacyResolve(result);
        _privacyResolve = null;
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') close(null);
        if (e.key === 'Enter') btnSave.click();
    };

    seg.onclick = (e) => {
        const t = e.target.closest('.seg-btn');
        if (!t) return;
        buttons.forEach((b) => b.classList.remove('active'));
        t.classList.add('active');
    };

    btnCancel.onclick = () => close(null);
    btnSave.onclick = () => {
        const active = seg.querySelector('.seg-btn.active');
        const isPublic = Boolean(active && active.dataset.value === 'public');
        close(isPublic);
    };

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', onKeyDown);

    return new Promise((resolve) => { _privacyResolve = resolve; });
}

export function openConfirmModal({
    title = 'Please confirm',
    message = '',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    danger = false,
} = {}) {
    const modal = document.getElementById('confirmModal');
    const titleEl = document.getElementById('confirmTitle');
    const bodyEl = document.getElementById('confirmBody');
    const cancelBtn = document.getElementById('confirmCancel');
    const okBtn = document.getElementById('confirmOk');

    if (!modal || !titleEl || !bodyEl || !cancelBtn || !okBtn) {
        return Promise.resolve(false);
    }

    titleEl.textContent = title;
    bodyEl.textContent = message;
    cancelBtn.textContent = cancelText;
    okBtn.textContent = confirmText;
    okBtn.classList.toggle('danger', Boolean(danger));

    const close = (result = false) => {
        modal.classList.add('hidden');
        modal.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', onKeyDown);
        cancelBtn.onclick = null;
        okBtn.onclick = null;
        if (_confirmResolve) _confirmResolve(result);
        _confirmResolve = null;
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') close(false);
        if (e.key === 'Enter') close(true);
    };

    cancelBtn.onclick = () => close(false);
    okBtn.onclick = () => close(true);

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    okBtn.focus();
    document.addEventListener('keydown', onKeyDown);

    return new Promise((resolve) => { _confirmResolve = resolve; });
}

export function openTextPromptModal({
    title = 'Edit value',
    label = 'Value',
    value = '',
    placeholder = '',
    confirmText = 'Save',
    cancelText = 'Cancel',
    requireNonEmpty = true,
} = {}) {
    const modal = document.getElementById('textPromptModal');
    const titleEl = document.getElementById('textPromptTitle');
    const labelEl = document.getElementById('textPromptLabel');
    const inputEl = document.getElementById('textPromptInput');
    const cancelBtn = document.getElementById('textPromptCancel');
    const saveBtn = document.getElementById('textPromptSave');

    if (!modal || !titleEl || !labelEl || !inputEl || !cancelBtn || !saveBtn) {
        return Promise.resolve(null);
    }

    titleEl.textContent = title;
    labelEl.textContent = label;
    inputEl.value = value || '';
    inputEl.placeholder = placeholder || '';
    cancelBtn.textContent = cancelText;
    saveBtn.textContent = confirmText;

    const close = (result = null) => {
        modal.classList.add('hidden');
        modal.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', onKeyDown);
        cancelBtn.onclick = null;
        saveBtn.onclick = null;
        if (_textPromptResolve) _textPromptResolve(result);
        _textPromptResolve = null;
    };

    const submit = () => {
        const nextValue = String(inputEl.value || '').trim();
        if (requireNonEmpty && !nextValue) {
            showToast('Value required', 'Please enter a value.', 2200);
            _focusInput(inputEl, { force: false });
            return;
        }
        close(nextValue);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') close(null);
        if (e.key === 'Enter') submit();
    };

    cancelBtn.onclick = () => close(null);
    saveBtn.onclick = submit;

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    const promptFocused = _focusInput(inputEl, { force: false });
    if (promptFocused) {
        inputEl.select();
    }
    document.addEventListener('keydown', onKeyDown);

    return new Promise((resolve) => { _textPromptResolve = resolve; });
}

function _billingContextCurrentUserId() {
    return String(state.userId || localStorage.getItem('mosaic_user_id') || '').trim();
}

function _normalizeBillingReturnContext(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const kind = String(raw.kind || '').trim().toLowerCase();
    const jobId = String(raw.job_id || '').trim();
    if (!jobId) return null;
    if (kind !== 'subscription') return null;
    const longEdgePx = Number(raw.long_edge_px || 0);
    return {
        kind,
        user_id: String(raw.user_id || '').trim(),
        job_id: jobId,
        long_edge_px: (Number.isFinite(longEdgePx) && longEdgePx > 0) ? Math.round(longEdgePx) : 0,
        plan_code: String(raw.plan_code || '').trim().toLowerCase(),
        source: String(raw.source || '').trim(),
        checkout_url: String(raw.checkout_url || '').trim(),
        checkout_intent_id: String(raw.checkout_intent_id || '').trim(),
        saved_at: Number(raw.saved_at || 0),
    };
}

function _saveBillingReturnContext(rawContext) {
    const normalized = _normalizeBillingReturnContext(rawContext);
    if (!normalized) return;
    const payload = {
        ...normalized,
        user_id: _billingContextCurrentUserId(),
        saved_at: Date.now(),
    };
    try {
        localStorage.setItem(BILLING_RETURN_CONTEXT_KEY, JSON.stringify(payload));
    } catch (_e) {}
}

function _escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function _normalizePlanSort(plans) {
    const weight = { free: 0, paid: 1 };
    return [...(plans || [])]
        .filter((plan) => {
            const code = String((plan && plan.plan_code) || '').toLowerCase();
            return code === 'free' || code === 'paid';
        })
        .sort((a, b) => {
            const ak = String((a && a.plan_code) || '').toLowerCase();
            const bk = String((b && b.plan_code) || '').toLowerCase();
            return (weight[ak] ?? 99) - (weight[bk] ?? 99);
        });
}

function _renderPlanComparisonTable(payload) {
    const plans = _normalizePlanSort((payload && payload.plans) || []);
    if (!plans.length) return '<div class="hint">Plan data unavailable.</div>';

    const headers = plans.map((plan, index) => {
        const code = String(plan.plan_code || '').toLowerCase();
        const name = String(plan.name || code || 'Plan');
        const current = Boolean(plan.current);
        const bestBadge = code === 'paid'
            ? '<span class="plan-chip plan-chip-best">Best for frequent exporters</span>'
            : '';
        const currentBadge = current
            ? '<span class="plan-chip plan-chip-current">Current</span>'
            : '';
        return `
            <th data-plan-col="${index}">
                <div class="plan-col-head">
                    <strong>${_escapeHtml(name)}</strong>
                    ${bestBadge}
                    ${currentBadge}
                </div>
            </th>
        `;
    }).join('');

    const planCopy = {
        free: {
            monthlyPrice: 'free',
            mosaicConfiguration: 'Modify Mosaic settings freely',
            webExport: '10/day',
            homePrint: '$9.99 each',
            posterPrint: '$25.99 each',
            largeFormat: '$55.99 each',
            libraries: '2',
            photosPerLibrary: '1.000',
            retention: '10 days since generation',
            recentMosaics: '10',
            priorityQueue: 'no',
        },
        paid: {
            monthlyPrice: '$16.99',
            mosaicConfiguration: 'Modify Mosaic settings freely',
            webExport: '50/day',
            homePrint: '30 free exports per month, then $6.99 each',
            posterPrint: '1 free export per month, then $17.99 each',
            largeFormat: '$39.99 each',
            libraries: '5',
            photosPerLibrary: '10.000',
            retention: '365 days since generation',
            recentMosaics: '60',
            priorityQueue: 'yes',
        },
    };
    const valueFor = (plan, key) => {
        const code = String((plan && plan.plan_code) || '').toLowerCase();
        const copy = planCopy[code] || {};
        return String(copy[key] || '—');
    };

    const featureRows = [
        { label: 'Monthly price', key: 'monthlyPrice' },
        { label: 'Mosaic Configuration', key: 'mosaicConfiguration' },
        { label: 'Web export', key: 'webExport' },
        { label: 'Home print export', key: 'homePrint' },
        { label: 'Poster print export', key: 'posterPrint' },
        { label: 'Large format export', key: 'largeFormat' },
        { label: 'Number of libraries', key: 'libraries' },
        { label: 'Max number of photos in a library', key: 'photosPerLibrary' },
        { label: 'How long is a Mosaic kept in the cloud', key: 'retention' },
        { label: 'Max number of My Mosaics kept in the cloud', key: 'recentMosaics' },
        { label: 'Priority queue', key: 'priorityQueue' },
    ];

    const body = featureRows.map((row) => {
        const cols = plans.map((plan, index) => `<td data-plan-col="${index}">${_escapeHtml(valueFor(plan, row.key))}</td>`).join('');
        return `<tr><th>${_escapeHtml(row.label)}</th>${cols}</tr>`;
    }).join('');

    return `
        <table class="plan-compare-table" aria-label="Plan comparison">
            <thead>
                <tr>
                    <th>Feature</th>
                    ${headers}
                </tr>
            </thead>
            <tbody>
                ${body}
            </tbody>
        </table>
    `;
}

export async function openPlanComparisonModal(options = {}) {
    const modal = document.getElementById('planCompareModal');
    const titleEl = document.getElementById('planCompareTitle');
    const tableWrap = document.getElementById('planCompareTableWrap');
    const sub = document.getElementById('planCompareSub');
    const closeBtn = document.getElementById('planCompareClose');
    const upgradeBtn = document.getElementById('planCompareUpgrade');
    if (!modal || !titleEl || !tableWrap || !sub || !closeBtn || !upgradeBtn) {
        return { action: 'unavailable' };
    }
    const source = String((options && options.source) || 'unknown');
    const sourceLower = source.toLowerCase();
    const useDownloadCopy = sourceLower.includes('download');
    const preferredPlan = String((options && options.preferredPlan) || '').trim().toLowerCase();
    const returnContext = (options && typeof options.returnContext === 'object')
        ? options.returnContext
        : null;
    const rawCloseAction = String((options && options.closeAction) || '').trim();
    const requestedContinueWithDownload = ['continue_one_time', 'continue_with_download', 'continue_download']
        .includes(rawCloseAction.toLowerCase());
    const closeAction = rawCloseAction || (useDownloadCopy
        ? (requestedContinueWithDownload ? 'continue_with_download' : 'back_to_download')
        : 'close');
    const defaultCloseLabel = useDownloadCopy
        ? (requestedContinueWithDownload ? 'Continue with the Download' : 'Back to Download')
        : 'Close';
    const closeLabel = String((options && options.closeLabel) || defaultCloseLabel).trim() || defaultCloseLabel;
    const subtitle = String((options && options.subtitle) || '').trim();
    const defaultDownloadSubtitle = 'Paid includes monthly export credits and priority queue. You can still buy a one-time export for this mosaic without subscribing.';
    const resolvedSubtitle = useDownloadCopy
        ? (subtitle || defaultDownloadSubtitle)
        : subtitle;
    const upgradeCtaLabel = useDownloadCopy ? 'Upgrade to Paid' : 'Upgrade';
    API.logUiFlow('billing', 'plan_compare_open', 'ok', { source }).catch(() => {});

    let payload = null;
    let selectedPlanCode = preferredPlan || '';
    let closeReason = closeAction;
    const close = (result = { action: 'close' }) => {
        modal.classList.add('hidden');
        modal.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', onKeyDown);
        closeBtn.onclick = null;
        upgradeBtn.onclick = null;
        tableWrap.onclick = null;
        if (_planCompareResolve) _planCompareResolve(result);
        _planCompareResolve = null;
    };
    const onKeyDown = (event) => {
        if (event.key === 'Escape') close({ action: 'close' });
    };

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    titleEl.textContent = 'Compare plans';
    sub.textContent = useDownloadCopy ? resolvedSubtitle : 'Loading plan comparison...';
    tableWrap.innerHTML = '<div class="hint">Loading plans...</div>';
    document.addEventListener('keydown', onKeyDown);
    closeBtn.textContent = closeLabel;
    closeBtn.onclick = () => {
        API.logUiFlow('billing', 'plan_compare_cta', 'ok', {
            source,
            cta: closeReason,
        }).catch(() => {});
        close({ action: closeReason });
    };
    upgradeBtn.disabled = true;
    upgradeBtn.textContent = upgradeCtaLabel;

    try {
        payload = await API.getBillingPlanComparison();
    } catch (e) {
        tableWrap.innerHTML = '<div class="hint">Could not load plan data.</div>';
        sub.textContent = (e && e.message) ? e.message : 'Could not load plan data.';
        API.logUiFlow('billing', 'plan_compare_open', 'error', { source }).catch(() => {});
    }

    if (payload && Array.isArray(payload.plans)) {
        tableWrap.innerHTML = _renderPlanComparisonTable(payload);
        const currentPlan = String(payload.current_plan_code || payload.current_tier || '').toLowerCase();
        const suggested = String(payload.recommended_plan_code || '').toLowerCase();
        const plans = _normalizePlanSort(payload.plans);
        const planCodes = plans.map((p) => String((p && p.plan_code) || '').toLowerCase());
        const fallbackTarget = preferredPlan || suggested || 'paid';
        selectedPlanCode = planCodes.includes(fallbackTarget) ? fallbackTarget : (planCodes.includes('paid') ? 'paid' : '');
        const tableEl = tableWrap.querySelector('.plan-compare-table');

        const setSelectedColumn = (colIdx) => {
            if (!tableEl || !Number.isFinite(colIdx) || colIdx < 0) return;
            tableEl.querySelectorAll('[data-plan-col]').forEach((node) => {
                const nodeIdx = Number(node.getAttribute('data-plan-col'));
                node.classList.toggle('is-selected', nodeIdx === colIdx);
            });
            tableEl.querySelectorAll('.plan-col-head').forEach((node, idx) => {
                node.classList.toggle('is-selected', idx === colIdx);
            });
        };

        const checkoutAllowed = (code) => {
            const row = plans.find((p) => String((p && p.plan_code) || '').toLowerCase() === code);
            return Boolean(row && row.checkout_available);
        };

        const targetName = (() => {
            const row = plans.find((p) => String((p && p.plan_code) || '').toLowerCase() === selectedPlanCode);
            return String((row && row.name) || selectedPlanCode || 'selected');
        })();

        const selectedIndex = Math.max(0, plans.findIndex((p) => String((p && p.plan_code) || '').toLowerCase() === selectedPlanCode));
        setSelectedColumn(selectedIndex);

        if (!selectedPlanCode || !checkoutAllowed(selectedPlanCode)) {
            upgradeBtn.disabled = true;
            upgradeBtn.textContent = useDownloadCopy ? upgradeCtaLabel : 'Checkout unavailable';
            if (!useDownloadCopy) {
                sub.textContent = 'Checkout is not configured for your next plan yet.';
            }
        } else if (selectedPlanCode === currentPlan) {
            upgradeBtn.disabled = true;
            upgradeBtn.textContent = useDownloadCopy ? upgradeCtaLabel : 'Already active';
            if (!useDownloadCopy) {
                sub.textContent = `${targetName} is already active on your account.`;
            }
        } else {
            upgradeBtn.disabled = false;
            upgradeBtn.textContent = useDownloadCopy ? upgradeCtaLabel : `Upgrade to ${targetName}`;
            sub.textContent = useDownloadCopy
                ? resolvedSubtitle
                : (resolvedSubtitle || `Current plan: ${(currentPlan || 'free').toUpperCase()}. Compare features and upgrade when ready.`);
        }

        tableWrap.onclick = (event) => {
            const cell = event.target && event.target.closest ? event.target.closest('[data-plan-col]') : null;
            if (!cell) return;
            const colIdx = Number(cell.getAttribute('data-plan-col'));
            if (!Number.isFinite(colIdx) || colIdx < 0) return;
            const picked = plans[colIdx];
            const pickedCode = String((picked && picked.plan_code) || '').toLowerCase();
            if (!pickedCode) return;
            selectedPlanCode = pickedCode;
            setSelectedColumn(colIdx);
            const pickedName = String((picked && picked.name) || pickedCode);
            if (!picked.checkout_available) {
                upgradeBtn.disabled = true;
                upgradeBtn.textContent = useDownloadCopy ? upgradeCtaLabel : 'Checkout unavailable';
                return;
            }
            if (pickedCode === currentPlan) {
                upgradeBtn.disabled = true;
                upgradeBtn.textContent = useDownloadCopy ? upgradeCtaLabel : 'Already active';
                return;
            }
            upgradeBtn.disabled = false;
            upgradeBtn.textContent = useDownloadCopy ? upgradeCtaLabel : `Upgrade to ${pickedName}`;
        };

        upgradeBtn.onclick = async () => {
            if (upgradeBtn.disabled || !selectedPlanCode) return;
            API.logUiFlow('billing', 'plan_compare_cta', 'ok', {
                source,
                plan_code: selectedPlanCode,
                cta: 'upgrade',
            }).catch(() => {});
            upgradeBtn.disabled = true;
            const prevText = upgradeBtn.textContent;
            upgradeBtn.textContent = 'Opening checkout...';
            try {
                const response = await API.createSubscriptionCheckout(selectedPlanCode);
                if (response && response.requires_payment && response.checkout_url) {
                    if (returnContext) {
                        _saveBillingReturnContext({
                            kind: 'subscription',
                            user_id: _billingContextCurrentUserId(),
                            job_id: String(returnContext.job_id || '').trim(),
                            long_edge_px: Number(returnContext.long_edge_px || 0),
                            plan_code: selectedPlanCode,
                            source: String(returnContext.source || source || '').trim(),
                            checkout_url: String(response.checkout_url || ''),
                            checkout_intent_id: String(response.checkout_intent_id || ''),
                        });
                    }
                    showToast('Redirecting to checkout', 'Paid plan checkout is opening...', 2200);
                    close({ action: 'checkout', plan_code: selectedPlanCode });
                    window.location.href = response.checkout_url;
                    return;
                }
                if (response && response.reason === 'already_subscribed') {
                    showToast('Plan active', 'This plan is already active on your account.', 2400);
                    close({ action: 'already_subscribed', plan_code: selectedPlanCode });
                    return;
                }
                showToast('Checkout unavailable', 'Could not open checkout right now.', 3200);
            } catch (e) {
                showToast('Checkout failed', (e && e.message) ? e.message : 'Could not open checkout.', 3600);
                API.logUiFlow('billing', 'plan_compare_cta', 'error', {
                    source,
                    plan_code: selectedPlanCode,
                    cta: 'upgrade',
                }).catch(() => {});
            } finally {
                upgradeBtn.disabled = false;
                upgradeBtn.textContent = prevText;
            }
        };
    }

    return new Promise((resolve) => { _planCompareResolve = resolve; });
}
